import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import driverLicenseStatusFilters from 'Measure/constants/driverLicenseStatusFilters';
import getAllDriverLicenseControls from 'Measure/api/driver-license/getAllDriverLicenseControls';
import DriverLicenseControlsGrid from 'Measure/components/vehicle/driver-license/DriverLicenseControlsGrid';
import getDriverLicenseControl from 'Measure/api/driver-license/getDriverLicenseControl';
import useAxiosRequest from 'Common/hooks/useAxiosRequest';
import axios from 'axios';
import notify from 'Common/utils/notify';
import { toast } from 'react-toastify';
import { orderBy } from 'lodash';

const loadProperties = [
    'employee',
    'driverLicense',
    'driverLicense.nextCheck',
    'driverLicense.previousCheck',
];

const filterBySearchTerm = (driverLicenseControls, searchTerm) =>
    driverLicenseControls.filter(driverLicenseControl => {
        const employee = driverLicenseControl.employee;

        const searchTerms = searchTerm
            .toLowerCase()
            .split(' ')
            .filter(term => !!term);

        const firstName = employee.firstName.toLowerCase();
        const isFirstNameMatched =
            searchTerms.findIndex(term => firstName.startsWith(term)) !== -1;

        const lastName = employee.lastName.toLowerCase();
        const isLastNameMatched =
            searchTerms.findIndex(term => lastName.startsWith(term)) !== -1;

        return searchTerms.length > 1
            ? isFirstNameMatched && isLastNameMatched
            : isFirstNameMatched || isLastNameMatched;
    });

const AllDriverLicenseControlsTab = ({
    searchTerm,
    selectedStatus,
    onDriverLicenseControlsFound,
}) => {
    const driverLicenseControlsRequest = useCallback(
        cancelToken => getAllDriverLicenseControls(loadProperties, cancelToken),
        [],
    );

    const {
        data: allDriverLicenseControls,
        setData: setAllDriverLicenseControls,
        loadData: loadAllDriverLicenseControls,
        isLoading,
        hasError,
    } = useAxiosRequest(driverLicenseControlsRequest, []);

    const nonArchivedDriverLicenseControls = useMemo(
        () =>
            allDriverLicenseControls.filter(
                driverLicenseControl =>
                    !driverLicenseControl.employee.isArchived,
            ),
        [allDriverLicenseControls],
    );

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const handleDriverLicenseControlStatusChange = async driverLicenseControl => {
        try {
            const response = await getDriverLicenseControl(
                driverLicenseControl.id,
                loadProperties,
                source.token,
            );

            setAllDriverLicenseControls(prevAllDriverLicenseControls => {
                const newDriverLicenseControls = [
                    ...prevAllDriverLicenseControls,
                ];

                const index = newDriverLicenseControls.findIndex(
                    control => control.id === driverLicenseControl.id,
                );

                if (index !== -1) {
                    newDriverLicenseControls[index] = response.data;
                }

                return newDriverLicenseControls;
            });
        } catch (error) {
            if (!axios.isCancel(error)) {
                notify(
                    'Ein Fehler ist aufgetreten. Bitte versuchen Sie, die Seite zu aktualisieren.',
                    {
                        type: toast.TYPE.ERROR,
                    },
                );
            }
        }
    };

    const driverLicenseControls = useMemo(() => {
        let filteredDriverLicenseControls = orderBy(
            nonArchivedDriverLicenseControls,
            ['employee.lastName', 'employee.firstName'],
            ['asc', 'asc'],
        );

        if (searchTerm) {
            filteredDriverLicenseControls = filterBySearchTerm(
                filteredDriverLicenseControls,
                searchTerm,
            );
        }

        if (selectedStatus === driverLicenseStatusFilters.ACTIVE) {
            filteredDriverLicenseControls = filteredDriverLicenseControls.filter(
                driverLicenseControl => driverLicenseControl.isActive,
            );
        } else if (selectedStatus === driverLicenseStatusFilters.INACTIVE) {
            filteredDriverLicenseControls = filteredDriverLicenseControls.filter(
                driverLicenseControl => !driverLicenseControl.isActive,
            );
        }

        return filteredDriverLicenseControls;
    }, [nonArchivedDriverLicenseControls, searchTerm, selectedStatus]);

    useEffect(() => {
        if (searchTerm) {
            onDriverLicenseControlsFound(
                filterBySearchTerm(
                    nonArchivedDriverLicenseControls,
                    searchTerm,
                ),
            );
        }
    }, [
        nonArchivedDriverLicenseControls,
        onDriverLicenseControlsFound,
        searchTerm,
    ]);

    return (
        <DriverLicenseControlsGrid
            isLoading={isLoading}
            hasError={hasError}
            onReload={loadAllDriverLicenseControls}
            driverLicenseControls={driverLicenseControls}
            onDriverLicenseControlActivate={
                handleDriverLicenseControlStatusChange
            }
            onDriverLicenseControlDeactivate={
                handleDriverLicenseControlStatusChange
            }
        />
    );
};

AllDriverLicenseControlsTab.propTypes = {
    searchTerm: PropTypes.string.isRequired,
    selectedStatus: PropTypes.string.isRequired,
    onDriverLicenseControlsFound: PropTypes.func.isRequired,
};

export default AllDriverLicenseControlsTab;
